import { useMutation, type FetchResult } from "@apollo/client";

import type {
  DeleteServiceInput,
  DeleteServiceMutation,
  DeleteServiceMutationVariables,
  GetServicesByNodeQuery,
  Service,
} from "../../../API";
import { DELETE_SERVICE } from "../../../common/operations/mutations";
import { GET_SERVICES_BY_NODE } from "../../../common/operations/queries";
import { servicesByNodeVariable } from "../../devices/variables/devices";
import { useSyncShadow } from "./useSyncShadow";
import { successNotification } from "../../../common/variables/notification";
import { ServiceTypeEnum } from "../variables/modelManager";

type DeleteServiceInputExtended = DeleteServiceInput & {
  serviceName: string;
};
interface DeleteServiceInterface {
  deleteService: (
    input: DeleteServiceInputExtended
  ) => Promise<FetchResult<DeleteServiceMutation>>;
  data?: DeleteServiceMutation | null;
  loading: boolean;
}

export const useDeleteService = (): DeleteServiceInterface => {
  const { syncShadowsForNode } = useSyncShadow();

  const [removeService, { data, loading }] = useMutation<
    DeleteServiceMutation,
    DeleteServiceMutationVariables
  >(DELETE_SERVICE);

  const deleteService = async (
    input: DeleteServiceInputExtended
  ): Promise<FetchResult<DeleteServiceMutation>> => {
    const nodeId = input.nodeId;

    return await removeService({
      variables: {
        input: {
          deviceId: input.deviceId,
          serviceId: input.serviceId,
          nodeId: input.nodeId,
        },
      },
      onCompleted() {
        let serviceName = input.serviceName;

        if (serviceName === ServiceTypeEnum.SAFETY_AND_SECURITY) {
          serviceName = ServiceTypeEnum.PERSONNEL_DETECTION;
        }

        successNotification(
          `Service ${serviceName} model deleted successfully`
        );
      },
      update(cache): void {
        const id = input.serviceId;
        const normalizedId = cache.identify({ id, __typename: "Service" });

        cache.evict({ id: normalizedId });

        cache.gc();

        const queryData = cache.readQuery<GetServicesByNodeQuery>({
          query: GET_SERVICES_BY_NODE,
          variables: { nodeId },
        });

        if (queryData) {
          const updatedServices = queryData.getServicesByNode.items.filter(
            service => service?.id !== id
          );

          cache.writeQuery({
            query: GET_SERVICES_BY_NODE,
            data: {
              getServicesByNode: {
                ...queryData.getServicesByNode,
                items: updatedServices,
              },
            },
          });

          servicesByNodeVariable(updatedServices as Service[]);
        }

        syncShadowsForNode(input.nodeId);
      },
      refetchQueries: ["GetZonesByDevice"],
    });
  };

  return { deleteService, data, loading };
};
